import {
    Modal,
    Pattern,
    Route,
    RouteName,
    RouteParams,
    RouteParamsList,
    Routes,
    Url,
} from './types'
import UrlPattern from 'url-pattern'
import {routes as appRoutes} from '../../app.json'

type PatternElements = {
    pattern: Url
    urlPattern: UrlPattern
    wildcardIndex?: string[]
}

class Router extends Map<RouteName, PatternElements> {
    static options = {
        segmentNameCharset: 'a-zA-Z0-9_',
        segmentValueCharset: 'a-zA-Z0-9-_~ %.',
    }

    routes: Routes

    constructor(routes: Routes) {
        super()

        this.routes = routes

        const wildcardRegExp = new RegExp(`\\*[${Router.options.segmentNameCharset}]*`, 'gu')

        for (const [name, pattern] of Object.entries(Object.assign({}, ...Object.values(routes))) as [RouteName, Pattern][]) {
            const stockPattern = pattern.replace(wildcardRegExp, '*')
            const urlPattern = new UrlPattern(stockPattern, Router.options)

            const wildcardMatches = pattern.match(wildcardRegExp)
            const wildcardIndex = wildcardMatches?.map((m) => m.replace('*', ''))

            this.set(name, {
                pattern,
                urlPattern,
                ...wildcardIndex && {wildcardIndex},
            })
        }
    }

    compare(a: Route | null, b: Route | null) {
        if (a === null || b === null) {
            return a === b
        }

        return this.stringify(a.name, a.params) === this.stringify(b.name, b.params)
    }

    isModal(modal: Modal | Route): modal is Modal {
        return Object.keys(this.routes.modal).includes(modal.name)
    }

    isPrivate(name: RouteName) {
        return !Object.keys(this.routes.public).includes(name)
    }

    isPublic(name: RouteName) {
        return Object.keys(this.routes.public).includes(name)
    }

    isRoute<K extends keyof RouteParamsList>(name: K, route: Route | null): route is {name: K, params: RouteParamsList[K]} {
        return !!route && route.name === name && (this.isPrivate(route.name) || this.isPublic(route.name))
    }

    match(url: Url): Route | null {
        for (const [name, elements] of this) {
            const {
                urlPattern,
                wildcardIndex,
            } = elements

            const match = urlPattern.match(url)

            if (match !== null) {
                if (match._ && wildcardIndex) {
                    const wildcardValues = Array.isArray(match._) ? match._ : [match._]

                    for (const [i, wildcardName] of wildcardIndex.entries()) {
                        if (wildcardName) {
                            match[wildcardName] = wildcardValues[i]
                        }
                    }
                }

                return {
                    name,
                    params: match,
                }
            }
        }

        return null
    }

    stringify(name: RouteName, params?: RouteParams) {
        const {urlPattern} = this.get(name) ?? {}

        if (urlPattern) {
            try {
                return urlPattern.stringify(params)
            } catch {
                return null
            }
        }

        return null
    }

    validate(route: unknown): route is Route {
        const isObject = (obj: unknown): obj is object => !!obj && typeof obj === 'object' && obj.constructor === Object
        const isRoute = (obj: object): obj is Route => 'name' in obj && typeof obj.name === 'string' &&
            (('params' in obj && isObject(obj.params)) || !('params' in obj)) // eslint-disable-line no-extra-parens

        if (!isObject(route)) {
            return false
        }

        if (!isRoute(route)) {
            return false
        }

        return !!this.stringify(route.name, route.params)
    }
}

export default new Router(appRoutes)
